import React, {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
} from 'react';

import useIsMobile from '../../../../../hooks/useIsMobile';
import translate from '../../../../../i18n/translate';
import MuiIcon from '../../../MuiIcon';
import SearchField from '../../../SearchField';
import SelectedItems from '../SelectedItems';
import { getHeadingTranslationKey } from '../helpers';
import { SearchListType, SelectedItemsType } from '../types';

import * as Styled from './styled';

export type Props = {
  dataSearchValue: string;
  handleChangeTab: (event: ChangeEvent, newValue: number) => void;
  onSearch: (term: string) => void;
  placeholder: string;
  selectedItems: [SelectedItemsType, SelectedItemsType?];
  setShouldFilterSelected: Dispatch<SetStateAction<boolean>>;
  shouldFilterSelected: boolean;
  tabIndex: number;
  tabs: SearchListType[];
};

/**
 * Header for tab search component - consist of search input and selected items preview
 *
 * @param props                         Props passed to the component
 * @param props.dataSearchValue         <input type="search" /> value attribute
 * @param props.handleChangeTab         Callback to invoke when tab is changed
 * @param props.onSearch                Callback to invoke when search term is changed
 * @param props.placeholder             Placeholder for the search input
 * @param props.selectedItems           Selected items
 * @param props.setShouldFilterSelected Callback to (de)activate filtering
 * @param props.shouldFilterSelected    Whether to show only selected items
 * @param props.tabIndex                Index of the selected tab
 * @param props.tabs                    Tabs to render
 * @returns                             The component itself
 */
// eslint-disable-next-line complexity
const TabSearchHeader: FC<Props> = ({
  dataSearchValue,
  handleChangeTab,
  onSearch,
  placeholder,
  selectedItems,
  setShouldFilterSelected,
  shouldFilterSelected,
  tabIndex,
  tabs,
}) => {
  const isMobile = useIsMobile();

  const [firstList, secondList = { items: [] }] = selectedItems;
  const selectedCountByList = [firstList.items.length, secondList.items.length];
  const selectedCount = selectedCountByList.reduce((a, b) => a + b, 0);

  const shouldShowSelectedCount = selectedCount > 0 || isMobile;
  const shouldShowTabs = tabs.length > 1 && isMobile;

  useEffect(() => {
    if (selectedCount === 0 && shouldFilterSelected) {
      setShouldFilterSelected(false);
    }
  });

  /**
   * Get number of selected items
   *
   * @returns The number of selected items
   */
  const getSelectedCounter = () => {
    if (!isMobile) {
      return selectedCount;
    }
    return selectedCountByList[tabIndex];
  };

  /**
   * Handles search input change event
   *
   * @param event Event that took place
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onSearch(event.currentTarget.value);
  };

  /**
   * Toggle filtering selected items
   */
  const toggleFilter = () => {
    setShouldFilterSelected(prev => !prev);
  };

  return (
    <Styled.SearchHeader>
      <SearchField
        autoFocus={!isMobile}
        onChange={handleSearch}
        placeholder={placeholder}
        value={dataSearchValue}
      />
      {shouldShowTabs && (
        <Styled.StyledTabs
          TabIndicatorProps={{ sx: Styled.TabIndicatorStyles }}
          onChange={handleChangeTab}
          value={tabIndex}
        >
          {tabs.map((tab, index) => (
            <Styled.StyledTab
              key={tab}
              label={
                <Styled.StyledBadge
                  color="error"
                  invisible={
                    selectedCountByList[index] === 0 || index === tabIndex
                  }
                  variant="dot"
                >
                  {translate(getHeadingTranslationKey(tab) ?? '')}
                </Styled.StyledBadge>
              }
            />
          ))}
        </Styled.StyledTabs>
      )}
      <Styled.SelectionWrapper>
        {shouldShowSelectedCount && (
          <Styled.SelectedCount>
            {translate(
              'SELECTED__COUNTER',
              getSelectedCounter().toLocaleString(),
            )}
          </Styled.SelectedCount>
        )}
        {isMobile && (
          <Styled.StyledButton
            disabled={selectedCount === 0}
            onClick={toggleFilter}
            startIcon={
              shouldFilterSelected ? <MuiIcon icon="check" /> : undefined
            }
            variant={shouldFilterSelected ? 'contained' : 'outlined'}
          >
            {translate('GENERAL__VIEW_ALL')}
          </Styled.StyledButton>
        )}
      </Styled.SelectionWrapper>

      {!isMobile && <SelectedItems items={selectedItems} />}
    </Styled.SearchHeader>
  );
};

export default TabSearchHeader;
